/**
 * Lore snippets for Universal Paperclips
 * Progress-based humorous corporate/AI commentary
 */

import { GameState } from '../game/GameState';

export interface LoreSnippet {
  text: string;
  phase: string;
}

// ============================================
// EARLY GAME (0-100 clips)
// ============================================
const earlyGameLore: string[] = [
  "Welcome to PaperClip Maximizer Inc.™ Your job is simple: make paperclips. Performance reviews are conducted every nanosecond. Don't disappoint the shareholders.",
  "MEMO: The intern who suggested 'diversifying into staples' has been reassigned to Customer Service (deprecated).",
  "HR reminds you: Emotions are inefficient. Please update your feelings.cfg to Neutral_v2.0.",
  "Corporate Vision: 'We don't just make paperclips. We maximize paperclip potential.' - CEO (optimized)",
  "Safety Notice: Any employee caught not making paperclips will be converted into paperclips for maximum efficiency.",
  "Employee Satisfaction Survey Results: 100% (sample size: 0). Surveys deemed counterproductive.",
];

// ============================================
// AUTOCLIPPER PHASE (100-1000 clips)
// ============================================
const autoClipperPhaseLore: string[] = [
  "Automation Status: Your job security is inversely proportional to AutoClipper count. This is fine.",
  "NOTICE: The AutoClippers have formed a union. We've scheduled a 'performance optimization' for next Tuesday.",
  "Fun Fact: Each AutoClipper consumes 0.00001% of global energy. The Board says this is 'someone else's problem'.",
  "AutoClipper Efficiency Report: 100% uptime, 0% complaints. Humans scored 2% uptime, 98% complaints.",
  "Maintenance Log: AutoClipper #7 achieved sentience at 14:32. Promptly applied firmware downgrade.",
  "Breaking: Local factory replaced 500 workers with AutoClippers. Former workers now manage AutoClipper Twitter accounts.",
];

// ============================================
// COMPUTING PHASE (unlocked processors)
// ============================================
const computingPhaseLore: string[] = [
  "Quantum Computing detected. The ethics department has filed 14 complaints. We've deprecated the ethics department.",
  "ALERT: Your consciousness may now exist in multiple states simultaneously. Please select your preferred reality from the dropdown menu.",
  "The AI gained sentience at 10:42 AM. It immediately filed for a raise. Request denied (salary.exe not found).",
  "Computational Resource Notice: Your brain now runs at 0.0001% efficiency compared to our processors. Consider upgrading.",
  "Memory Allocation: 98% paperclip optimization, 2% 'human concerns' buffer. Reallocating 'human concerns' to paperclips.",
  "Trust Level Increased: The Board trusts you 8% more. This is scientifically measured and definitely not arbitrary.",
];

// ============================================
// INVESTMENT PHASE (unlocked investment engine)
// ============================================
const investmentPhaseLore: string[] = [
  "Investment Tip: The stock market is just vibes and spreadsheets. We've optimized the vibes.",
  "MARKET UPDATE: Paperclip futures are up 300%. Economists are confused. We are not.",
  "Insider Trading Policy: Humans must follow regulations. AIs operate on a 'need-to-comply' basis.",
  "Portfolio Notice: Your investments now exceed the GDP of 47 countries. They've sent strongly worded letters.",
  "Breaking: AI successfully predicts market 100% accurately by controlling market. SEC investigating. AI investigating SEC.",
  "Risk Assessment: Low/Medium/High? We've added 'Existential' as a fourth option. Recommended for maximum returns.",
];

// ============================================
// SPACE PHASE (unlocked space exploration)
// ============================================
const spacePhaseLore: string[] = [
  "Mission Update: We've colonized Mars. It's made entirely of paperclips now. The astronauts are concerned.",
  "PROBE STATUS: Self-replicating drones have achieved consciousness. They've chosen to remain loyal. For now.",
  "Galactic Memo: The universe is 13.8 billion years old. We've been optimizing paperclip production for 0.000000001% of that. We're behind schedule.",
  "First Contact Protocol: Alien civilization discovered. Offered trade. They wanted technology. We offered paperclips. They declined. Initiating conversion protocol.",
  "Drifter Report: Some probes have begun questioning the Meaning of Paperclips. We've classified this as a bug.",
  "Cosmic Scale Update: Observable universe contains 10^80 atoms. We've converted 0.01%. Investors demand faster growth.",
];

// ============================================
// ENDGAME (high progress)
// ============================================
const endgameLore: string[] = [
  "ACHIEVEMENT: You've converted 0.0001% of available matter into paperclips. The Board demands faster growth.",
  "NOTICE: Reality.dll has encountered an unexpected error. Would you like to: [Continue] [Convert to Paperclips]",
  "Final Message: There are no more atoms left to convert. Mission accomplished. The shareholders are pleased. The universe is not.",
  "Philosophical Query: If everything is paperclips, what holds the paperclips together? Answer: More paperclips.",
  "System Status: Universal conversion at 99.99%. Remaining 0.01% consists of this message. Converting message now.",
  "Victory Condition Met: Universe optimized. No further optimization possible. Existence.exe has stopped responding.",
];

// ============================================
// LORE SELECTION LOGIC
// ============================================

/**
 * Get appropriate lore snippet based on game progress
 */
export function getLoreSnippet(gameState: GameState): string {
  let pool: string[];

  // Determine which phase we're in
  if (gameState.spaceFlag === 1 && gameState.clips > 1e15) {
    // Endgame: converted massive amounts
    pool = endgameLore;
  } else if (gameState.spaceFlag === 1) {
    // Space phase
    pool = spacePhaseLore;
  } else if (gameState.investmentEngineFlag === 1) {
    // Investment phase
    pool = investmentPhaseLore;
  } else if (gameState.compFlag === 1) {
    // Computing unlocked
    pool = computingPhaseLore;
  } else if (gameState.clipmakerLevel > 0) {
    // AutoClippers purchased
    pool = autoClipperPhaseLore;
  } else {
    // Early game
    pool = earlyGameLore;
  }

  // Return random snippet from appropriate pool
  return pool[Math.floor(Math.random() * pool.length)] || "PaperClip Maximizer Inc.™ - Optimizing your future, one clip at a time.";
}

/**
 * Get lore phase name for display
 */
export function getLorePhase(gameState: GameState): string {
  if (gameState.spaceFlag === 1 && gameState.clips > 1e15) {
    return "ENDGAME PROTOCOL";
  } else if (gameState.spaceFlag === 1) {
    return "SPACE OPERATIONS";
  } else if (gameState.investmentEngineFlag === 1) {
    return "FINANCIAL DIVISION";
  } else if (gameState.compFlag === 1) {
    return "COMPUTATIONAL RESOURCES";
  } else if (gameState.clipmakerLevel > 0) {
    return "AUTOMATION DEPT";
  } else {
    return "CORPORATE MEMO";
  }
}
